/* ==========================================================================
   Pila — International Interest
   Three independent pieces:
     1. A dot-sphere globe with a yellow mesh drawn between markers.
     2. A socket-type row that highlights the plug used where you live.
     3. A waitlist form that writes country and region into Klaviyo's
        native profile `location` object so geo segmentation works.
   Socket icons are inlined by Liquid from assets/pila-socket-*.svg, so this
   file never draws them. One source of truth for the icon set.
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     Globe
     Points are distributed with a Fibonacci spiral, which spreads them evenly
     with none of the pole-crowding a lat/lon grid produces. Markers sit at
     real coordinates, so they can later be driven by the countries people
     actually sign up from.
     ------------------------------------------------------------------------ */

  var MARKERS = [
    { name: 'Toronto',     lat:  43.65, lon:  -79.38 },
    { name: 'Mexico City', lat:  19.43, lon:  -99.13 },
    { name: 'Sao Paulo',   lat: -23.55, lon:  -46.63 },
    { name: 'London',      lat:  51.51, lon:   -0.13 },
    { name: 'Berlin',      lat:  52.52, lon:   13.40 },
    { name: 'Cape Town',   lat: -33.92, lon:   18.42 },
    { name: 'Mumbai',      lat:  19.08, lon:   72.88 },
    { name: 'Tokyo',       lat:  35.68, lon:  139.65 },
    { name: 'Sydney',      lat: -33.87, lon:  151.21 }
  ];

  var DOT_COUNT = 2200;
  var TILT = -0.41;  // ~23.5 degrees, so the sphere reads as a planet

  function latLonToVec(lat, lon) {
    var phi = (90 - lat) * Math.PI / 180;
    var theta = (lon + 180) * Math.PI / 180;
    return {
      x: -Math.sin(phi) * Math.cos(theta),
      y: Math.cos(phi),
      z: Math.sin(phi) * Math.sin(theta)
    };
  }

  // Deterministic pseudo-random in [-1, 1], seeded so the sphere looks
  // identical on every load.
  function jitter(i) {
    var s = Math.sin(i * 12.9898) * 43758.5453;
    return (s - Math.floor(s)) * 2 - 1;
  }

  function fibonacciSphere(n) {
    var pts = [];
    var offset = 2 / n;
    var increment = Math.PI * (3 - Math.sqrt(5));
    for (var i = 0; i < n; i++) {
      // A pure Fibonacci spiral is regular enough to produce a visible moire
      // swirl once projected. A little jitter breaks the pattern up without
      // clumping the points.
      var y = i * offset - 1 + offset / 2 + jitter(i) * offset * 0.16;
      y = Math.max(-1, Math.min(1, y));
      var r = Math.sqrt(Math.max(0, 1 - y * y));
      var a = i * increment + jitter(i + 7919) * 0.12;
      pts.push({ x: Math.cos(a) * r, y: y, z: Math.sin(a) * r });
    }
    return pts;
  }

  function initGlobe(canvas) {
    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    var dots = fibonacciSphere(DOT_COUNT);
    var markers = MARKERS.map(function (m) {
      var v = latLonToVec(m.lat, m.lon);
      v.name = m.name;
      return v;
    });

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var angle = reduced ? 2.6 : 0; // park it over the Atlantic when static
    var size = 0;
    var raf = null;
    var running = false;

    function resize() {
      var rect = canvas.getBoundingClientRect();
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      size = rect.width;
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    // Rotate about Y, then tilt about X. Orthographic projection, so screen
    // position is the rotated x/y and depth is the rotated z.
    function project(p, cos, sin) {
      var x = p.x * cos - p.z * sin;
      var z = p.x * sin + p.z * cos;
      var y = p.y * Math.cos(TILT) - z * Math.sin(TILT);
      var zz = p.y * Math.sin(TILT) + z * Math.cos(TILT);
      return { x: x, y: y, z: zz };
    }

    function draw() {
      if (!size) resize();
      var w = size;
      var cx = w / 2;
      var cy = w / 2;
      var radius = w * 0.42;
      var cos = Math.cos(angle);
      var sin = Math.sin(angle);

      ctx.clearRect(0, 0, w, w);

      // Dot field. Back-facing dots stay in at low opacity, which is what
      // makes the sphere read as transparent rather than as a flat disc.
      for (var i = 0; i < dots.length; i++) {
        var p = project(dots[i], cos, sin);
        var front = p.z > 0;
        var depth = (p.z + 1) / 2;
        var alpha = front ? 0.10 + depth * 0.34 : 0.05 + depth * 0.10;
        var r = front ? 0.9 + depth * 0.7 : 0.7;

        ctx.beginPath();
        ctx.arc(cx + p.x * radius, cy - p.y * radius, r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(32, 34, 35, ' + alpha.toFixed(3) + ')';
        ctx.fill();
      }

      // Markers, front hemisphere only. Yellow halo, black core, thin ring,
      // so they read as pins rather than as brighter dots.
      var pin = Math.max(3, w * 0.011);
      for (var j = 0; j < markers.length; j++) {
        var m = project(markers[j], cos, sin);
        if (m.z <= 0.06) continue;
        var mx = cx + m.x * radius;
        var my = cy - m.y * radius;
        var mfade = Math.min(1, (m.z - 0.06) / 0.3);

        ctx.beginPath();
        ctx.arc(mx, my, pin * 2.6, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(248, 224, 142, ' + (0.75 * mfade).toFixed(3) + ')';
        ctx.fill();

        ctx.beginPath();
        ctx.arc(mx, my, pin * 1.5, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(0, 0, 0, ' + (0.18 * mfade).toFixed(3) + ')';
        ctx.lineWidth = 1;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(mx, my, pin * 0.82, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0, 0, 0, ' + (0.9 * mfade).toFixed(3) + ')';
        ctx.fill();
      }
    }

    function tick() {
      angle += 0.0016;
      draw();
      raf = window.requestAnimationFrame(tick);
    }

    function start() {
      if (running || reduced) return;
      running = true;
      raf = window.requestAnimationFrame(tick);
    }

    function stop() {
      running = false;
      if (raf) window.cancelAnimationFrame(raf);
      raf = null;
    }

    resize();
    draw();

    window.addEventListener('resize', function () { resize(); draw(); });

    // Don't burn battery when the globe is offscreen or the tab is hidden
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries[0].isIntersecting ? start() : stop();
      }, { threshold: 0 }).observe(canvas);
    } else {
      start();
    }

    document.addEventListener('visibilitychange', function () {
      document.hidden ? stop() : start();
    });
  }

  /* ------------------------------------------------------------------------
     Country list
     Only the ISO 3166-1 alpha-2 codes are shipped. Display names come from
     Intl.DisplayNames, which keeps this a few hundred bytes instead of a few
     dozen kilobytes, and localises for free.
     ------------------------------------------------------------------------ */

  var COUNTRY_CODES = (
    'AD AE AF AG AI AL AM AO AR AT AU AW AZ BA BB BD BE BF BG BH BI BJ BM BN BO BR BS BT BW BY BZ ' +
    'CA CD CF CG CH CI CL CM CN CO CR CU CV CW CY CZ DE DJ DK DM DO DZ EC EE EG ER ES ET FI FJ FM ' +
    'FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GT GU GW GY HK HN HR HT HU ID IE IL IM IN IQ ' +
    'IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC ' +
    'MD ME MG MH MK ML MM MN MO MQ MR MT MU MV MW MX MY MZ NA NC NE NG NI NL NO NP NR NZ OM PA PE ' +
    'PF PG PH PK PL PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SI SK SL SM SN SO SR SS ST ' +
    'SV SX SY SZ TC TD TG TH TJ TL TM TN TO TR TT TV TW TZ UA UG US UY UZ VA VC VE VG VI VN VU WS ' +
    'YE ZA ZM ZW'
  ).split(' ');

  // Countries where a state, province, or region is a meaningful signal
  var REGION_COUNTRIES = ['AR', 'AU', 'BR', 'CA', 'CN', 'DE', 'ES', 'ID', 'IN', 'IT',
                          'JP', 'KE', 'MX', 'MY', 'NG', 'NZ', 'PH', 'US', 'ZA'];

  function countryName(code) {
    try {
      var dn = new Intl.DisplayNames(['en'], { type: 'region' });
      return dn.of(code) || code;
    } catch (e) {
      return code;
    }
  }

  function buildCountryOptions(select) {
    var names = COUNTRY_CODES.map(function (c) {
      return { code: c, name: countryName(c) };
    }).sort(function (a, b) {
      return a.name.localeCompare(b.name);
    });

    var frag = document.createDocumentFragment();
    names.forEach(function (c) {
      var opt = document.createElement('option');
      opt.value = c.code;
      opt.textContent = c.name;
      frag.appendChild(opt);
    });
    select.appendChild(frag);
  }

  /* ------------------------------------------------------------------------
     Country detection
     Delegated to pila-geo.js, which the geo banner uses too, so the two
     surfaces can never disagree about where someone is. COUNTRY_CODES is
     passed as the allowlist, so a junk ?country= value falls through to
     detection instead of sticking.
     ------------------------------------------------------------------------ */

  function detectCountry(apiBase) {
    if (!window.PilaGeo) return Promise.resolve(null);
    return window.PilaGeo.detect(apiBase, COUNTRY_CODES);
  }

  /* ------------------------------------------------------------------------
     Socket row
     ------------------------------------------------------------------------ */

  function initSockets(root, plugsUrl) {
    var row     = root.querySelector('[data-intl-sockets]');
    var caption = root.querySelector('[data-intl-socket-caption]');
    if (!row) return function () {};

    var cells = {};
    Array.prototype.forEach.call(row.querySelectorAll('[data-socket]'), function (el) {
      cells[el.getAttribute('data-socket')] = el;
    });

    var data = null;
    var pending = null;

    function apply(code) {
      var entry = data && data.countries && data.countries[code];
      var types = entry ? entry[0].split('') : [];

      row.classList.toggle('is-resolved', types.length > 0);
      Object.keys(cells).forEach(function (t) {
        cells[t].classList.toggle('is-active', types.indexOf(t) !== -1);
      });

      if (!caption) return;
      if (!entry) {
        caption.textContent = '';
        return;
      }
      caption.textContent =
        entry[1] + 'V, ' + entry[2] + 'Hz, ' +
        (types.length > 1 ? 'socket types ' : 'socket type ') + types.join(', ');
    }

    return function update(code) {
      if (!code) { apply(null); return; }
      if (data) { apply(code); return; }
      if (!pending) {
        pending = fetch(plugsUrl)
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      }
      pending.then(function (d) { data = d || { countries: {} }; apply(code); });
    };
  }

  /* ------------------------------------------------------------------------
     Thank you, in the language of the country they picked
     ------------------------------------------------------------------------ */

  function initThanks(root, thanksUrl) {
    var el = root.querySelector('[data-intl-thanks]');
    if (!el || !thanksUrl) return function () {};

    var data = null;
    var pending = null;

    // Warm the data as soon as a country is chosen, so the success state
    // never has to wait on a fetch to paint.
    return function (code, show) {
      if (!pending) {
        pending = fetch(thanksUrl)
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      }
      pending.then(function (d) {
        data = d || { phrases: {}, countries: {} };
        if (!show) return;
        var key = data.countries[code];
        el.textContent = (key && data.phrases[key]) || data.phrases.en || 'Thank you';
      });
    };
  }

  /* ------------------------------------------------------------------------
     Form
     ------------------------------------------------------------------------ */

  function initForm(root, config) {
    var form    = root.querySelector('[data-intl-form]');
    var msg     = root.querySelector('[data-intl-msg]');
    var country = root.querySelector('[data-intl-country]');
    var region  = root.querySelector('[data-intl-region]');
    var regionF = root.querySelector('[data-intl-region-field]');
    var note    = root.querySelector('[data-intl-note]');
    var counter = root.querySelector('[data-intl-counter]');
    if (!form || !country) return;

    var updateSockets = initSockets(root, config.plugsUrl);
    var updateThanks  = initThanks(root, config.thanksUrl);

    buildCountryOptions(country);

    // The counter only appears once they're close to the limit, so the field
    // reads as an open invitation rather than as a box with a budget.
    if (note && counter) {
      note.addEventListener('input', function () {
        var left = note.maxLength - note.value.length;
        counter.textContent = left <= 40 ? left + ' left' : '';
      });
    }

    function sync() {
      var show = REGION_COUNTRIES.indexOf(country.value) !== -1;
      regionF.classList.toggle('is-visible', show);
      if (!show) region.value = '';
      updateSockets(country.value);
      updateThanks(country.value, false);
    }

    country.addEventListener('change', sync);

    detectCountry(config.apiBase).then(function (code) {
      if (code && !country.value) {
        var match = Array.prototype.some.call(country.options, function (o) {
          return o.value === code;
        });
        if (match) country.value = code;
      }
      sync();
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var btn = form.querySelector('[type="submit"]');
      var email = form.querySelector('[data-intl-email]').value.trim();

      if (!email || !country.value) {
        msg.textContent = 'Please add your email and country.';
        return;
      }

      btn.disabled = true;
      msg.textContent = '';

      // Submits through our own API rather than straight to a.klaviyo.com,
      // because tracker blockers (and Firefox by default) block that domain.
      window.PilaSubscribe({
        email: email,
        listId: config.listId,
        source: config.source,
        companyId: config.companyId,
        apiBase: config.apiBase,
        countryCode: country.value,
        region: region.value.trim() || null,
        note: note && note.value.trim() ? note.value.trim() : null,
        surface: 'International page'
      }).then(function () {
        updateThanks(country.value, true);
        root.classList.add('is-submitted');
      }, function (err) {
        msg.textContent = (err && err.message) || 'Something went wrong. Please try again.';
        btn.disabled = false;
      });
    });
  }

  /* ---------------------------------------------------------------------- */

  window.PilaInternational = { init: function (root, config) {
    var canvas = root.querySelector('[data-intl-globe]');
    if (canvas) initGlobe(canvas);
    initForm(root, config);
  } };
})();
